import apiClient from 'services/_infra/apiClient';
import {
  isFemaCredentialFacts,
  isFemaCredentialInspectAnswer,
  isFemaCredentialStoreAnswer,
  isFemaTenantRefusal,
  isFemaTenantSummary,
  isFemaTenantValidateAnswer,
  type FemaCredentialFacts,
  type FemaCredentialInspectAnswer,
  type FemaCredentialStoreAnswer,
  type FemaTenantRefusal,
  type FemaTenantSummary,
  type FemaTenantValidateAnswer,
} from 'types/femaTenant.types';

/**
 * FE-11 tenant credential calls that do not fit RTK Query: the two multipart uploads (inspect and
 * store). `axiosBaseQuery` does not pass `onUploadProgress`, and a credential upload is a one-off
 * command, so these calls use `apiClient` directly (AGENTS.md "API and Service Files").
 *
 * This file also holds ONE reader per Fusion edge route (`/fusion/v2/fema/tenants…`,
 * MULTICOG-CONTRACT §5 "Fusion edge mirror"). The edge answers bare objects. It never wraps an
 * answer in `{ result }`. Each reader accepts exactly one shape, with no extra key (the edge types
 * are `.strict()`). Any other shape is malformed. The tests mock `apiClient`.
 *
 * Passwords go in the multipart BODY only, never in a URL or a query string. This file never logs
 * a request, a body or an error object.
 */

export const FEMA_TENANTS_PATH = '/v2/fema/tenants';

/** Contract §5: the credential file parts. A JKS keystore, or the PEM set (chain optional). */
export type FemaCredentialFiles =
  | { kind: 'keystore'; keystore: File }
  | { kind: 'pem'; certificate: File; key: File; chain?: File | null };

/** Contract §5 `ZCredentialUploadFields` (the passwords are read from a component ref). */
export type FemaCredentialSecrets = {
  keystorePassword?: string;
  privateKeyPassword?: string;
};

export type FemaCredentialUploadRequest = {
  tenantId: number;
  files: FemaCredentialFiles;
  secrets: FemaCredentialSecrets;
  keyAlias?: string;
  onProgress?: (fraction: number) => void;
};

export type FemaCredentialStoreRequest = FemaCredentialUploadRequest & {
  /** The fingerprint that the person saw and confirmed on the Review step. */
  confirmSha256: string;
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// ---------------------------------------------------------------------------
// The key sets of the edge answers (contract §5; Fusion-Backend `femaTenantEdge.types.ts`, all
// `.strict()`). A key that is not in the set makes the answer malformed.
// ---------------------------------------------------------------------------

/** `ZCredentialFacts`: the six certificate facts, `daysToExpiry` and `expiryBand`. Nothing else. */
const FACTS_KEYS = [
  'subject',
  'issuer',
  'serialNumber',
  'sha256',
  'notBefore',
  'notAfter',
  'daysToExpiry',
  'expiryBand',
] as const;
const REPLACEMENT_KEYS = [...FACTS_KEYS, 'validatedAt'] as const;
const INSPECTION_KEYS = [
  'subject',
  'issuer',
  'serialNumber',
  'sha256',
  'notBefore',
  'notAfter',
  'daysToExpiry',
  'keyAlgorithm',
  'chainLength',
  'cogMatches',
] as const;
const SUMMARY_KEYS = [
  'id',
  'environment',
  'isProduction',
  'cogId',
  'cogName',
  'enabled',
  'credentialState',
  'daysToExpiry',
  'expiryBand',
  'lastValidatedAt',
  'failedStage',
  'active',
  'replacement',
] as const;
const VALIDATION_KEYS = ['state', 'at', 'slot', 'stage', 'error'] as const;
const STORED_CREDENTIAL_KEYS = ['slot', 'generation', 'status', 'certificate'] as const;
const RETIRED_CREDENTIAL_KEYS = ['sha256', 'notAfter'] as const;
const REFUSAL_KEYS = ['code', 'message', 'stage', 'findings'] as const;

/** True when every own key of `value` is in `keys`. The type guards check that each key is present. */
const hasOnlyKeys = (value: unknown, keys: readonly string[]): value is UnknownRecord =>
  isRecord(value) && Object.keys(value).every((key) => keys.includes(key));

const isExactFacts = (value: unknown): value is FemaCredentialFacts =>
  isFemaCredentialFacts(value) && hasOnlyKeys(value, FACTS_KEYS);

/** `ZFemaTenantSummary`, exact: no extra key on the summary, the active facts or the replacement. */
export const isExactFemaTenantSummary = (value: unknown): value is FemaTenantSummary =>
  isFemaTenantSummary(value) &&
  hasOnlyKeys(value, SUMMARY_KEYS) &&
  (value.active === null || hasOnlyKeys(value.active, FACTS_KEYS)) &&
  (value.replacement === null || hasOnlyKeys(value.replacement, REPLACEMENT_KEYS));

const isExactRefusal = (value: unknown): value is FemaTenantRefusal =>
  isFemaTenantRefusal(value) && hasOnlyKeys(value, REFUSAL_KEYS);

// ---------------------------------------------------------------------------
// ONE reader per route. `null` means "malformed": the caller shows the contract refusal.
// ---------------------------------------------------------------------------

/**
 * `GET /tenants` → `{ tenants: FemaTenantSummary[] }`. An empty list is a valid answer (the
 * workplace holds no tenant). One malformed row makes the whole answer malformed: the page never
 * hides a class that the workplace holds.
 */
export const readFemaTenantList = (response: unknown): FemaTenantSummary[] | null => {
  if (!hasOnlyKeys(response, ['tenants']) || !Array.isArray(response.tenants)) return null;
  const rows: unknown[] = response.tenants;
  return rows.every(isExactFemaTenantSummary) ? rows : null;
};

/** `GET /tenants/:id` → a bare `FemaTenantSummary`. */
export const readFemaTenant = (response: unknown): FemaTenantSummary | null =>
  isExactFemaTenantSummary(response) ? response : null;

/** `POST /tenants/:id/enable` → `{ tenant }`. Answers the tenant. */
export const readFemaTenantEnableAnswer = (response: unknown): FemaTenantSummary | null =>
  hasOnlyKeys(response, ['tenant']) && isExactFemaTenantSummary(response.tenant)
    ? response.tenant
    : null;

/** `POST /tenants/:id/rotate` → `{ tenant, retiredCredential: { sha256, notAfter } }`. Answers the tenant. */
export const readFemaTenantRotateAnswer = (response: unknown): FemaTenantSummary | null =>
  hasOnlyKeys(response, ['tenant', 'retiredCredential']) &&
  isExactFemaTenantSummary(response.tenant) &&
  hasOnlyKeys(response.retiredCredential, RETIRED_CREDENTIAL_KEYS) &&
  typeof response.retiredCredential.sha256 === 'string' &&
  typeof response.retiredCredential.notAfter === 'string'
    ? response.tenant
    : null;

/** `POST /tenants/:id/validate` → `{ tenant, validation: { state, at, slot, stage, error } }`. */
export const readFemaTenantValidateAnswer = (response: unknown): FemaTenantValidateAnswer | null =>
  isFemaTenantValidateAnswer(response) &&
  hasOnlyKeys(response, ['tenant', 'validation']) &&
  isExactFemaTenantSummary(response.tenant) &&
  hasOnlyKeys(response.validation, VALIDATION_KEYS)
    ? response
    : null;

/** `POST /tenants/:id/credential/inspect` → `{ inspection, stored: false }`. */
export const readFemaCredentialInspectAnswer = (
  response: unknown,
): FemaCredentialInspectAnswer | null =>
  isFemaCredentialInspectAnswer(response) &&
  hasOnlyKeys(response, ['inspection', 'stored']) &&
  hasOnlyKeys(response.inspection, INSPECTION_KEYS)
    ? response
    : null;

/** `POST /tenants/:id/credential` → `{ credential: { slot, generation, status, certificate }, tenant }`. */
export const readFemaCredentialStoreAnswer = (
  response: unknown,
): FemaCredentialStoreAnswer | null =>
  isFemaCredentialStoreAnswer(response) &&
  hasOnlyKeys(response, ['credential', 'tenant']) &&
  hasOnlyKeys(response.credential, STORED_CREDENTIAL_KEYS) &&
  isExactFacts(response.credential.certificate) &&
  isExactFemaTenantSummary(response.tenant)
    ? response
    : null;

/** A refusal body `{ code, message, stage?, findings? }`, exact. */
export const readFemaTenantRefusal = (body: unknown): FemaTenantRefusal | null =>
  isExactRefusal(body) ? body : null;

/** Builds the multipart body. Passwords go in the body only. */
export const buildFemaCredentialFormData = (
  request: FemaCredentialUploadRequest & { confirmSha256?: string },
): FormData => {
  const formData = new FormData();
  const { files, secrets, keyAlias, confirmSha256 } = request;
  if (files.kind === 'keystore') {
    formData.append('keystore', files.keystore, files.keystore.name);
  } else {
    formData.append('certificate', files.certificate, files.certificate.name);
    formData.append('key', files.key, files.key.name);
    if (files.chain) formData.append('chain', files.chain, files.chain.name);
  }
  if (secrets.keystorePassword) formData.append('keystorePassword', secrets.keystorePassword);
  if (secrets.privateKeyPassword) formData.append('privateKeyPassword', secrets.privateKeyPassword);
  if (keyAlias) formData.append('keyAlias', keyAlias);
  if (confirmSha256) formData.append('confirmSha256', confirmSha256);
  return formData;
};

/** A refusal the caller can show: code, sentence and stage. Never a value. */
export class FemaTenantRequestError extends Error {
  readonly code: string;
  readonly stage: FemaTenantRefusal['stage'] | null;
  readonly status: number | null;

  constructor(refusal: FemaTenantRefusal, status: number | null) {
    super(refusal.message);
    this.name = 'FemaTenantRequestError';
    this.code = refusal.code;
    this.stage = refusal.stage ?? null;
    this.status = status;
  }
}

/** The refusal for an answer that does not match its route shape. */
export const FEMA_TENANT_UNEXPECTED_ANSWER: FemaTenantRefusal = {
  code: 'UNEXPECTED_ANSWER',
  message: 'The server answer does not match the credential contract.',
};

const REQUEST_FAILED: FemaTenantRefusal = {
  code: 'REQUEST_FAILED',
  message: 'The credential request failed. Try again.',
};

/** Reads an Axios failure into a `FemaTenantRequestError`. It keeps no request data. */
export const toFemaTenantRequestError = (error: unknown): FemaTenantRequestError => {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null;
  const status = response && typeof response.status === 'number' ? response.status : null;
  const refusal = response ? readFemaTenantRefusal(response.data) : null;
  return new FemaTenantRequestError(refusal ?? REQUEST_FAILED, status);
};

/**
 * Reads any failure of a tenant call into `{ code, message, stage }`: a `FemaTenantRequestError`
 * from this file, or an RTK Query error from `axiosBaseQuery` (`{ status, message, raw }`, where
 * `raw` is the bare refusal body). It keeps no request data, so no password can reach the screen
 * through it.
 */
export const toFemaTenantRefusal = (error: unknown): FemaTenantRefusal => {
  if (error instanceof FemaTenantRequestError) {
    return {
      code: error.code,
      message: error.message,
      ...(error.stage ? { stage: error.stage } : {}),
    };
  }
  const refusal = isRecord(error) ? readFemaTenantRefusal(error.raw) : null;
  if (refusal) {
    return {
      code: refusal.code,
      message: refusal.message,
      ...(refusal.stage ? { stage: refusal.stage } : {}),
    };
  }
  return REQUEST_FAILED;
};

const postCredential = async (
  url: string,
  formData: FormData,
  onProgress?: (fraction: number) => void,
): Promise<unknown> => {
  try {
    const response = await apiClient.post(url, formData, {
      // The shared client defaults to JSON. Clearing it lets Axios add the multipart boundary.
      headers: { 'Content-Type': undefined },
      onUploadProgress: (event: { progress?: number }) => {
        if (onProgress && typeof event?.progress === 'number') onProgress(event.progress);
      },
    });
    return response?.data;
  } catch (error) {
    throw toFemaTenantRequestError(error);
  }
};

/** `POST /tenants/:id/credential/inspect`: reads the credential and stores nothing. */
export const inspectFemaCredential = async (
  request: FemaCredentialUploadRequest,
): Promise<FemaCredentialInspectAnswer> => {
  const answer = readFemaCredentialInspectAnswer(
    await postCredential(
      `${FEMA_TENANTS_PATH}/${request.tenantId}/credential/inspect`,
      buildFemaCredentialFormData(request),
      request.onProgress,
    ),
  );
  if (!answer) throw new FemaTenantRequestError(FEMA_TENANT_UNEXPECTED_ANSWER, null);
  return answer;
};

/** `POST /tenants/:id/credential`: stores the credential the person confirmed by fingerprint. */
export const storeFemaCredential = async (
  request: FemaCredentialStoreRequest,
): Promise<FemaCredentialStoreAnswer> => {
  const answer = readFemaCredentialStoreAnswer(
    await postCredential(
      `${FEMA_TENANTS_PATH}/${request.tenantId}/credential`,
      buildFemaCredentialFormData(request),
      request.onProgress,
    ),
  );
  if (!answer) throw new FemaTenantRequestError(FEMA_TENANT_UNEXPECTED_ANSWER, null);
  return answer;
};
